import React from 'react';
import {StyledPaper, StyledSelectbox, StyledSelectErrorMessage, StyledTagName} from "./select.style";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { StyledTextbox } from '../Textbox/Textbox.style';
import { Warning as WarningIcon } from '@mui/icons-material';
import { CircularProgress, TextFieldProps } from '@mui/material';
import { sanitizeInput } from 'utils/functions';


export interface IStyledSelectBoxProps {
  id?: string;
  multiple?: boolean;
  handleChange?: any,
  handleSearchChange?: (value: string) => void;
  defaultValue?: any
  placeholder: string;
  options?: any;
  value: Object;
  label: string;
  boldLabel: boolean;
  error: boolean;
  required?: boolean;
  errorMessage?: string;
  showErrorIcon?: boolean;
  style?: any;
  disableClearable?: boolean;
  valueKey?: string;
  [rest: string]: any;
  locale?: any;
  freeSolo?: boolean;
  textboxToolTip?: string;
  isSpecialCharacterAllowed?: boolean;
  optionLabel?: string;
  InputProps?: TextFieldProps;
  inputProps?: any;
}

const SelectBox: React.FC<IStyledSelectBoxProps> = (props): JSX.Element => {
  const {
    id,
    multiple,
    handleChange,
    handleSearchChange,
    defaultValue,
    placeholder,
    options,
    value,
    label,
    boldLabel,
    error,
    required,
    errorMessage,
    showErrorIcon,
    style,
    disableClearable,
    valueKey = 'value',
    freeSolo,
    locale,
    textboxToolTip,
    isSpecialCharacterAllowed = true,
    optionLabel = 'name',
    InputProps,
    inputProps,
    ...rest
  } = props;

  const freeSoloProps = freeSolo
    ? {
        getOptionLabel: undefined,
        getOptionSelected: undefined,
        renderOption: undefined,
      }
    : {};

  return (
      <StyledSelectbox
        freeSolo={freeSolo}
        style={style}
        id={id}
        error={error}
        multiple={multiple}
        disableClearable={disableClearable}
        options={options}
        value={value}
        boldLabel={boldLabel}
        getOptionLabel={(option: any) => option?.[optionLabel] || ''}
        defaultValue={defaultValue}
        onChange={handleChange}
        filterOptions={handleSearchChange ? (x) => x : undefined}
        loadingText={<CircularProgress size={20} color="inherit" />}
        isOptionEqualToValue={(option: any, value: any) => value?.[valueKey] === option?.[valueKey]}
        PaperComponent={(props) => <StyledPaper {...props} />}
        renderOption={( props, option: any, { selected }) => (
          <li {...props}>
            {multiple && (<Checkbox
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              checked={selected}
            />)}
            {option?.[optionLabel]}<StyledTagName>{option.user_tagAOB}</StyledTagName>
          </li>
        )}
        renderInput={(params) => (<div>
         <StyledTextbox
            {...params}
            id={id}
            required={required}
            variant="outlined"
            label={label}
            placeholder={placeholder || locale?.selectBox?.placeholder}
            title={textboxToolTip || locale?.selectBox?.textboxToolTip}
            onChange={(event) => {
              sanitizeInput(event, isSpecialCharacterAllowed);
              handleSearchChange?.(event.target.value);
            }}
            inputProps={{
              ...params.inputProps,
              ...inputProps
            }}
            {...InputProps}
          />
          {error && !!errorMessage && (
            <StyledSelectErrorMessage>
              {showErrorIcon && <WarningIcon className="warning-icon" />}
              {errorMessage}
            </StyledSelectErrorMessage>
          )}              
         </div>
        )}
        componentsProps={{
          popupIndicator: {
            tabIndex: 0,
          },
        }}
        openText={locale?.selectBox?.open}
        closeText={locale?.selectBox?.close}
        clearText={locale?.selectBox?.clear}
        noOptionsText={locale?.selectBox?.noOptions}
        {...freeSoloProps}
        {...rest}
      />      
  );
};


export const defaultProps = {
  id: 'select',
  loading: false,
  multiple: false,
  required: false,
  disableClearable: false,
  placeholder:'',
  handleChange: (e: any, value: any) => { console.log('Value changed: ', value); },
  label: '',
  options: [],
  freeSolo: false,
  textboxToolTip: '',
};
SelectBox.defaultProps = defaultProps;

export default SelectBox;
